// Sprint-019 E5b — the typed in-UI help/tooltip copy map (design/
// sprint-019-info-icon-component.md §2.3, matching this codebase's
// existing `lib/*Copy.ts` centralization convention). Every string here is
// copied VERBATIM from `specs/sprint-019-inui-help-audit.md`'s "Proposed
// help copy" column (already CD-approved — the audit's own 2026-07-12
// CHANGES-REQUESTED sweep fixed row #14's banned `manager` noun and row
// #7's surfaced-verb mismatch before this file was written) — do not
// hand-edit a string here without updating that audit row first.
//
// `content` is ALWAYS a plain string (SA guardrail #2 / Security's E5
// gate condition): `InfoTooltip` never renders HTML, never
// `dangerouslySetInnerHTML`. `label` feeds the trigger's required,
// per-instance `aria-label` — generated here from the field each entry
// annotates so a screen-reader user tabbing through several triggers on
// one screen never hears the same "Info, button" twice (design spec §3.4).
//
// Keyed by audit row number's subject, not by screen — several audit rows
// share a component (e.g. row 8's "Flexible" cadence lives in the same
// file as row 7's "Take a break"), so a screen-based key would collide.

export interface HelpCopyEntry {
  /** Feeds `InfoTooltip`'s required `label` prop (-> `aria-label`). */
  label: string;
  /** Plain-string tooltip/help-box body — never markdown, never HTML. */
  content: string;
}

export const HELP_COPY = {
  // Audit #1 — Login flow / passkey enrollment heading.
  passkeyEnrollment: {
    label: "More about quick sign-in",
    content:
      "Quick sign-in lets you get in with just your face, fingerprint, or phone unlock — no password to type or remember. It only works on this device. You can skip this and use your password instead, any time.",
  },
  // Audit #2 — Team tab, "Everyone" roster (mobile) / read-only hint context.
  teamRosterReadOnly: {
    label: "More about the team roster",
    content:
      "You can check off your own pieces here. Everyone else's tasks are shown so you can see progress — they'll check off their own from their list.",
  },
  // Audit #4 — "Daily reminders" toggle.
  dailyReminders: {
    label: "More about daily reminders",
    content:
      "A morning note with what's on your list, and an afternoon nudge if anything's still waiting. Never says which task or who it's for — safe even if someone else picks up your phone. Turn off any time.",
  },
  // Audit #5 — "Safety task" toggle.
  safetyTask: {
    label: "More about the safety tag",
    content:
      "Flags this task with a safety tag so everyone helping can see it's important to get right — this doesn't send an alert or change who can do it.",
  },
  // Audit #6 — Admin > People, "Link status" column header.
  linkStatus: {
    label: "More about link status",
    content:
      "Shows whether this person has a working sign-in link right now. If it says 'No link yet' or 'Link stopped,' send them a new one from their profile.",
  },
  // Audit #7 — Chore editor, "Take a break from this chore".
  choreTakeABreak: {
    label: "More about taking a break",
    content:
      "Taking a break stops this chore from being handed out to anyone, but keeps everything about it saved — tap \"Start this chore again\" whenever you're ready. There's no way to remove a chore for good, only pause it.",
  },
  // Audit #8 — Chore editor, cadence-type picker "Flexible" option.
  cadenceFlexible: {
    label: "More about 'A few times a week'",
    content:
      "Use this for chores that don't need a fixed day — just a goal, like 'twice a week.' It'll show up under 'This week' until the goal's met, then reset the following week.",
  },
  // Audit #9 — Calendar, "moved"/skipped chip meaning.
  calendarMovedSkipped: {
    label: "More about moved and skipped days",
    content:
      "'Moved' means this chore was rescheduled — tap it to jump to its new day. A grayed-out chore was skipped for that day; tap it to see why or undo it.",
  },
  // Audit #10 — Reports, Overdue aging tab's absent date-range toolbar.
  reportsOverdueNoRange: {
    label: "More about the overdue report",
    content: "Overdue is always a right-now snapshot — there's no date range to pick for this one.",
  },
  // Audit #11 — Weather card, "stale" current-conditions indicator.
  weatherStale: {
    label: "More about this forecast",
    content:
      "This is the last forecast we were able to get — it may be a little out of date, but it's still the most recent one we have.",
  },
  // Audit #12 — "Who's helping" heading.
  whosHelping: {
    label: "More about who's helping",
    content:
      "Anyone you add here can see this whole project and check off tasks — but only you can add or remove helpers.",
  },
  // Audit #13 — "Leave this project" confirm dialog body (not a trigger —
  // fed straight into `ConfirmDialog`'s existing `body` prop).
  leaveProjectConsequence:
    "You'll stop seeing this project. Any tasks you already added or finished will stay — just no longer show up on your list.",
  // Audit #14 — assign-out gating on a project task, member-owner's
  // non-interactive (static) assignee chip.
  assignOutMemberOwner: {
    label: "More about who can hand this off",
    content:
      "Only your farm admin or someone who can hand out work can give this task to someone else — as the owner, you can still do it yourself or mark it done.",
  },
  // Audit #16 — Config > Farm Settings, "Reset to default". The exact
  // fallback value isn't shipped to the client (`SettingsFieldMetaOut`
  // carries only `source`/`locked`, never the env default itself) — this
  // is deliberately value-agnostic rather than embedding a bracketed
  // placeholder, the SAME precedent already established in this codebase
  // for an identical client-can't-know-the-value case
  // (`lib/weatherCopy.ts::WEATHER_RADAR_DEFAULT_LAYER_CAPTION`).
  configResetToDefault: {
    label: "More about resetting to the default",
    content:
      "This goes back to the farm-wide default — the value everyone gets unless you set your own.",
  },
  // Audit #17 — Animal Weights tab, weight entry unit confirmation.
  weightUnitPounds: "Weight in pounds (lb).",
} satisfies Record<string, HelpCopyEntry | string>;

export type HelpCopyKey = keyof typeof HELP_COPY;
